import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import styles from './EnterpriseDetails.module.scss'

import { isApiError } from '@/api'
import { disbandEnterprise, updateEnterpriseName } from '@/api/internal'
import Button from '@/components/Button'
import DialogWrapper from '@/components/Dialog'
import Input from '@/components/Input'
import { useToast } from '@/components/Toast/ToastContext'
import { enterpriseDetailsQueryKey, useEnterpriseDetails } from '@/hooks/useEnterpriseDetails'

interface EnterpriseDetailsProps {
  enterpriseId: string
  enterpriseName: string
  onDelete?: () => void
  onNameChange?: (name: string) => void
}

const EditableName = ({
  enterpriseId,
  name,
  onNameChange,
}: {
  enterpriseId: string
  name: string
  onNameChange?: (name: string) => void
}) => {
  const queryClient = useQueryClient()
  const { updateToast } = useToast()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(name)

  const mutation = useMutation({
    mutationFn: (next: string) => updateEnterpriseName(enterpriseId, next),
    onSuccess: (_, next) => {
      queryClient.invalidateQueries({ queryKey: enterpriseDetailsQueryKey(enterpriseId) })
      queryClient.invalidateQueries({ queryKey: ['enterpriseSearch'] })
      updateToast('Enterprise name updated', 'success')
      setEditing(false)
      onNameChange?.(next)
    },
    onError: (error) => {
      const detail = isApiError(error) ? error.detail : undefined
      updateToast(detail || 'Failed to update enterprise name', 'error')
    },
  })

  if (!editing) {
    return (
      <span className={styles.editableValue}>
        <span>{name}</span>
        <Button
          title="Edit"
          type="light"
          onClick={() => {
            setDraft(name)
            setEditing(true)
          }}
        />
      </span>
    )
  }

  const next = draft.trim()

  return (
    <span className={styles.editableValue}>
      <Input
        type="text"
        className={styles.editInput}
        value={draft}
        onInput={setDraft}
        placeholder="Enterprise name"
        maxLength={100}
        isDisabled={mutation.isPending}
      />
      <Button
        title="Save"
        type="dark"
        isLoading={mutation.isPending}
        isDisabled={!next}
        onClick={() => {
          if (next === name) {
            setEditing(false)
            return
          }
          mutation.mutate(next)
        }}
      />
      <Button title="Cancel" type="light" isDisabled={mutation.isPending} onClick={() => setEditing(false)} />
    </span>
  )
}

const EnterpriseDetails = ({ enterpriseId, enterpriseName, onDelete, onNameChange }: EnterpriseDetailsProps) => {
  const queryClient = useQueryClient()
  const { updateToast } = useToast()
  const { data: enterpriseDetails } = useEnterpriseDetails(enterpriseId)
  const status = enterpriseDetails?.data?.status
  const createdAt = enterpriseDetails?.data?.created_at
  const disbandedAt = enterpriseDetails?.data?.disbanded_at

  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [deleteReason, setDeleteReason] = useState('')

  const deleteMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => disbandEnterprise(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: enterpriseDetailsQueryKey(enterpriseId) })
      updateToast('Enterprise disbanded successfully', 'success')
      setShowDeleteDialog(false)
      setDeleteReason('')
      onDelete?.()
    },
    onError: (error) => {
      const detail = isApiError(error) ? error.detail : undefined
      updateToast(detail || 'Failed to disband enterprise', 'error')
    },
  })

  return (
    <div className={styles.detailsSection}>
      <div className={styles.header}>
        <span className={styles.label}>Details</span>
      </div>
      <div className={styles.row}>
        <span className={styles.key}>Name</span>
        <span className={styles.value}>
          <EditableName enterpriseId={enterpriseId} name={enterpriseName} onNameChange={onNameChange} />
        </span>
      </div>
      <div className={styles.row}>
        <span className={styles.key}>Enterprise ID</span>
        <span className={styles.value}>{enterpriseId}</span>
      </div>
      <div className={styles.row}>
        <span className={styles.key}>Status</span>
        <span className={styles.value}>{status ?? '—'}</span>
      </div>
      <div className={styles.row}>
        <span className={styles.key}>Created</span>
        <span className={styles.value}>{createdAt ? new Date(createdAt).toLocaleDateString() : '—'}</span>
      </div>
      {disbandedAt && (
        <div className={styles.row}>
          <span className={styles.key}>Disbanded</span>
          <span className={styles.value}>{new Date(disbandedAt).toLocaleDateString()}</span>
        </div>
      )}
      <div className={styles.row}>
        <span className={styles.key}>
          Disband Enterprise
          <span className={styles.hint}>
            Permanently disbands this enterprise, cancels the subscription, and removes all members
          </span>
        </span>
        <span className={styles.value}>
          <Button
            title="Disband"
            type="light"
            className={styles.deleteButton}
            onClick={() => setShowDeleteDialog(true)}
          />
        </span>
      </div>
      <DialogWrapper
        isOpen={showDeleteDialog}
        title="Disband Enterprise"
        subtitle={`Are you sure you want to disband "${enterpriseName}"? This will cancel the subscription, remove all members, and mark the enterprise as disbanded.`}
        close={() => {
          setShowDeleteDialog(false)
          setDeleteReason('')
        }}
      >
        <div className={styles.deleteDialogContent}>
          <label className={styles.deleteReasonLabel}>
            Reason (required)
            <textarea
              className={styles.deleteReasonInput}
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              placeholder="e.g. Accidental creation"
              maxLength={500}
              rows={2}
            />
          </label>
          <div className={styles.deleteDialogActions}>
            <Button
              title="Cancel"
              type="light"
              onClick={() => {
                setShowDeleteDialog(false)
                setDeleteReason('')
              }}
            />
            <Button
              title={deleteMutation.isPending ? 'Disbanding...' : 'Disband'}
              type="destructive"
              onClick={() => deleteMutation.mutate({ id: enterpriseId, reason: deleteReason })}
              isDisabled={!deleteReason.trim() || deleteMutation.isPending}
            />
          </div>
        </div>
      </DialogWrapper>
    </div>
  )
}

export default EnterpriseDetails
